/**
 * A visit's files (1.5.5): everything in the visit's own folder — the crew's photos, the report, the files staff
 * added — with a thumbnail or an icon, newest last, and Open the folder. "Add a file" (schedulers, co-administrators,
 * the administrator) takes photos, PDFs and videos (MP4, MOV; up to 300 MB and three minutes each, several at once);
 * large files go up in chunks with a progress bar and carry on after a lost connection. Nothing is ever deleted: a
 * wrong file gets the note "added by mistake" and stays.
 */
import { useId, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ADD_ACCEPT,
  ADD_WHERES,
  UPLOAD_CHUNK,
  checkAddFile,
  contentTypeOf,
  extOf,
  mediaOf,
  type AddFileProblem,
  type AddWhere,
  type PhotoWhen,
  type UploadProgress,
  type UploadStartResponse,
  type VisitFileKind,
  type VisitFileView,
  type VisitFilesResponse,
} from '@shared/visit-files';
import { mp4DurationSeconds } from '@shared/media';
import { apiFetch } from '@/lib/api';
import { uploadInChunks, type UploadTransport } from '@/lib/chunked-upload';
import { formatInstant } from '@/lib/dates';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';

const base = (kind: VisitFileKind, id: string) => `/api/visit-files/${kind}/${encodeURIComponent(id)}`;
// 1.6.7: a housekeeping visit's file is opened from the folder it is in (Before/, After/, or the visit's own)
const fileUrl = (kind: VisitFileKind, id: string, name: string, when?: PhotoWhen | null) => `${base(kind, id)}/file?name=${encodeURIComponent(name)}${when ? `&when=${when}` : ''}`;

/** Can this browser play the video itself? (A camera-roll HEVC .mov usually cannot — SharePoint plays it.) */
function playsHere(name: string): boolean {
  if (typeof document === 'undefined') return false;
  try {
    return document.createElement('video').canPlayType(contentTypeOf(name)) !== '';
  } catch {
    return false;
  }
}

async function videoSeconds(f: File): Promise<number | null> {
  return mp4DurationSeconds(async (s, l) => new Uint8Array(await f.slice(s, s + l).arrayBuffer()), f.size);
}

const resumeKey = (kind: VisitFileKind, id: string, f: File, where: AddWhere | null) => `abf.upload.${kind}.${id}.${f.name}.${f.size}.${f.lastModified}${where && where !== 'other' ? `.${where}` : ''}`;

function readResume(key: string): { uploadId: string; name: string } | null {
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? 'null') as { uploadId?: unknown; name?: unknown } | null;
    return v && typeof v.uploadId === 'string' && typeof v.name === 'string' ? { uploadId: v.uploadId, name: v.name } : null;
  } catch {
    return null;
  }
}

interface Going {
  key: string;
  name: string;
  sent: number;
  size: number;
  state: 'up' | 'done' | 'failed';
  why?: string;
}

const MEDIA_ICON = { photo: 'camera', pdf: 'file', video: 'play', other: 'file' } as const;

export function VisitFilesButton({ kind, id, label, compact = false }: { kind: VisitFileKind; id: string; label: string; compact?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  return (
    <>
      <Button
        size="sm"
        variant={compact ? 'ghost' : 'secondary'}
        aria-label={label}
        onClick={(e) => {
          opener.current = e.currentTarget;
          setOpen(true);
        }}
        data-testid={`files-${kind}-${id}`}
      >
        <Icon name="folder" />
        {t('files.button')}
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        {open ? (
          <SheetContent title={label} returnFocusTo={opener} data-testid="visit-files">
            <VisitFiles kind={kind} id={id} />
          </SheetContent>
        ) : null}
      </Sheet>
    </>
  );
}

export function VisitFiles({ kind, id }: { kind: VisitFileKind; id: string }) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const qc = useQueryClient();
  const uid = useId();
  const input = useRef<HTMLInputElement | null>(null);
  const [going, setGoing] = useState<Going[]>([]);
  const [problems, setProblems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  // 1.6.7: a housekeeping visit asks "Before or After?" (or Other) before a file is chosen — no answer is assumed
  const [where, setWhere] = useState<AddWhere | null>(null);
  const qk = ['visit-files', kind, id];
  const q = useQuery({ queryKey: qk, queryFn: () => apiFetch<VisitFilesResponse>(base(kind, id)) });

  const patch = (key: string, p: Partial<Going>) => setGoing((g) => g.map((x) => (x.key === key ? { ...x, ...p } : x)));

  async function add(files: File[]) {
    if (!files.length) return;
    if (q.data?.asksWhere && !where) {
      setProblems([t('ba167.whereFirst')]);
      announce(t('ba167.whereFirst'), 'assertive');
      if (input.current) input.current.value = '';
      return;
    }
    setBusy(true);
    const refused: string[] = [];
    const ready: { f: File; seconds: number | null }[] = [];
    for (const f of files) {
      const seconds = mediaOf(f.name) === 'video' ? await videoSeconds(f) : null;
      const p: AddFileProblem | null = checkAddFile({ name: f.name, size: f.size, durationSeconds: seconds });
      if (p) refused.push(t(`files.problem.${p}`, { name: f.name }));
      else ready.push({ f, seconds });
    }
    setProblems(refused);
    if (refused.length) announce(refused.join(' '), 'assertive');
    for (const { f, seconds } of ready) {
      const key = resumeKey(kind, id, f, where);
      setGoing((g) => [...g.filter((x) => x.key !== key), { key, name: f.name, sent: 0, size: f.size, state: 'up' }]);
      announce(t('files.uploading', { name: f.name, pct: 0 }));
      const transport: UploadTransport = {
        start: () => apiFetch<UploadStartResponse>(`${base(kind, id)}/uploads`, { method: 'POST', body: JSON.stringify({ fileName: f.name, size: f.size, durationSeconds: seconds, ...(q.data?.asksWhere && where ? { where } : {}) }) }),
        status: (uploadId) => apiFetch<UploadProgress>(`/api/visit-uploads/${uploadId}`),
        put: (uploadId, start, chunk) =>
          apiFetch<UploadProgress>(`/api/visit-uploads/${uploadId}?start=${start}`, { method: 'PUT', body: chunk, headers: { 'Content-Type': 'application/octet-stream' } }),
      };
      let lastPct = 0;
      try {
        const done = await uploadInChunks(f, transport, {
          chunkSize: UPLOAD_CHUNK,
          resume: readResume(key),
          onStarted: (u) => localStorage.setItem(key, JSON.stringify(u)),
          onProgress: (sent, size) => {
            patch(key, { sent });
            const pct = Math.floor((sent / size) * 100);
            if (pct - lastPct >= 25 && pct < 100) {
              lastPct = pct;
              announce(t('files.uploading', { name: f.name, pct }));
            }
          },
        });
        localStorage.removeItem(key);
        patch(key, { state: 'done', sent: f.size, name: done.name });
        announce(t('files.added', { name: done.name }));
      } catch (e) {
        const why = (e as Error).message;
        patch(key, { state: 'failed', why });
        announce(t('files.failed', { name: f.name, why }), 'assertive');
      }
    }
    setBusy(false);
    if (input.current) input.current.value = '';
    await qc.invalidateQueries({ queryKey: qk });
  }

  async function mistake(f: VisitFileView) {
    try {
      await apiFetch(`${base(kind, id)}/mistake`, { method: 'POST', body: JSON.stringify({ name: f.name }) });
      announce(t('files.mistakeDone', { name: f.name }));
      await qc.invalidateQueries({ queryKey: qk });
    } catch (e) {
      announce((e as Error).message, 'assertive');
    }
  }

  const when = (s: string) => formatInstant(s, i18n.language);

  if (q.isLoading) return <p className="help">{t('app.loading')}</p>;
  if (!q.data) {
    return (
      <p className="help" role="alert">
        {(q.error as Error | null)?.message}
      </p>
    );
  }
  const v = q.data;
  return (
    <div className="visit-files" data-testid="visit-files-body">
      <p className="help">{t('files.count', { count: v.files.length, folder: v.folder })}</p>
      {v.webUrl ? (
        <a className="btn secondary" href={v.webUrl} target="_blank" rel="noreferrer" data-testid="files-open-folder">
          <Icon name="folder" /> {t('files.openFolder')}
        </a>
      ) : (
        <p className="help" data-testid="files-folder-path">
          {t('files.folderPath', { folder: v.folder })}
        </p>
      )}

      {v.files.length ? (
        <ul className="file-list visit-file-list" data-testid="files-list">
          {v.files.map((f) => {
            const media = mediaOf(f.name);
            const href = f.webUrl ?? (v.canOpen ? fileUrl(kind, id, f.name, f.when) : null);
            const thumb = v.canOpen && media === 'photo' && extOf(f.name) !== 'heic';
            return (
              <li key={`${f.when ?? ''}/${f.name}`} data-testid="files-item" data-when={f.when ?? 'other'}>
                <span className="vf-main">
                  {thumb ? (
                    <img className="vf-thumb" src={fileUrl(kind, id, f.name, f.when)} alt="" width={48} height={48} loading="lazy" />
                  ) : (
                    <span className="vf-thumb vf-icon" aria-hidden="true">
                      <Icon name={MEDIA_ICON[media]} />
                    </span>
                  )}
                  <span className="vf-text">
                    <span className="vf-name">{f.name}</span>
                    <span className="help">
                      {f.when ? `${t(`ba167.where.${f.when}`)} · ` : ''}
                      {t(`files.media.${media}`)}
                      {f.addedBy ? ` · ${t('files.addedBy', { who: f.addedBy, when: f.addedAt ? when(f.addedAt) : '' })}` : ''}
                      {media === 'video' && !playsHere(f.name) ? ` · ${t('files.playsInSharePoint')}` : ''}
                    </span>
                    {f.mistake ? (
                      <span className="vf-mistake" data-testid="files-mistake-tag">
                        <Icon name="alert" width={14} height={14} /> {t('files.mistakeTag', { who: f.mistake.by })}
                      </span>
                    ) : null}
                  </span>
                </span>
                <span className="vf-actions">
                  {href ? (
                    <a className="btn secondary sm" href={href} target="_blank" rel="noreferrer">
                      {t('files.open')}
                      <span className="sr-only"> {f.name}</span>
                    </a>
                  ) : null}
                  {!f.mistake && f.addedBy ? (
                    <Button size="sm" variant="ghost" onClick={() => mistake(f)} data-testid="files-mistake">
                      {t('files.mistake')}
                      <span className="sr-only"> {f.name}</span>
                    </Button>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="help">{t('files.empty')}</p>
      )}

      <div className="vf-add">
        <h3 className="vf-h">{t('files.add')}</h3>
        <p className="help" id={`${uid}-help`}>
          {t('files.addHelp')}
        </p>
        {v.asksWhere ? (
          <fieldset className="field choice ba-where" data-testid="files-where">
            <legend>{t('ba167.whereLegend')}</legend>
            <div className="seg radios">
              {ADD_WHERES.map((w) => (
                <label key={w} className={where === w ? 'on' : undefined}>
                  <input
                    type="radio"
                    name={`${uid}-where`}
                    value={w}
                    checked={where === w}
                    onChange={() => {
                      setWhere(w);
                      setProblems([]);
                    }}
                    data-testid={`files-where-${w}`}
                  />
                  <span>{t(`ba167.where.${w}`)}</span>
                </label>
              ))}
            </div>
            <p className="help" id={`${uid}-where-help`}>
              {t('ba167.whereHelp')}
            </p>
          </fieldset>
        ) : null}
        <input
          ref={input}
          id={`${uid}-input`}
          className="sr-only"
          type="file"
          multiple
          accept={ADD_ACCEPT}
          aria-hidden="true"
          onChange={(e) => void add(Array.from(e.target.files ?? []))}
          data-testid="files-input"
          tabIndex={-1}
        />
        <Button variant="secondary" disabled={busy || (v.asksWhere && !where)} onClick={() => input.current?.click()} aria-describedby={v.asksWhere ? `${uid}-help ${uid}-where-help` : `${uid}-help`} data-testid="files-add">
          <Icon name="upload" /> {t('files.add')}
        </Button>
        {problems.length ? (
          <ul className="vf-problems" role="alert" data-testid="files-problems">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        ) : null}
        {going.length ? (
          <ul className="vf-going" data-testid="files-going">
            {going.map((g) => {
              const pct = g.size ? Math.floor((g.sent / g.size) * 100) : 0;
              return (
                <li key={g.key}>
                  <label htmlFor={`${uid}-${g.key}`} className="vf-name">
                    {g.state === 'done' ? t('files.added', { name: g.name }) : g.state === 'failed' ? t('files.failed', { name: g.name, why: g.why ?? '' }) : t('files.uploading', { name: g.name, pct })}
                  </label>
                  <progress id={`${uid}-${g.key}`} max={100} value={g.state === 'done' ? 100 : pct} />
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
